import React from "react";
import { BrandCard } from "./BrandCard";
import { Button } from "@/components";
import { Plus, Globe } from "lucide-react";
import type { Brand } from "../interfaces";

interface BrandListProps {
  brands: Brand[];
  onEdit: (brand: Brand) => void;
  onDelete: (brandId: number) => void;
  onCreateNew: () => void;
  onScrape: () => void;
  onView?: (brand: Brand) => void;
  isLoading?: boolean;
}

export const BrandList: React.FC<BrandListProps> = ({
  brands,
  onEdit,
  onDelete,
  onCreateNew,
  onScrape,
  onView,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">Loading brands...</p>
        </div>
      </div>
    );
  }

  if (brands.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Globe className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No brands yet</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Get started by creating a brand manually or scraping one from a website
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button onClick={onScrape} variant="outline">
              <Globe className="h-4 w-4 mr-2" />
              Scrape from Website
            </Button>
            <Button onClick={onCreateNew}>
              <Plus className="h-4 w-4 mr-2" />
              Create Brand
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Brands</h2>
          <p className="text-sm text-muted-foreground">
            Manage your brand information and assets
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <Button onClick={onScrape} variant="outline">
            <Globe className="h-4 w-4 mr-2" />
            Scrape from Website
          </Button>
          <Button onClick={onCreateNew}>
            <Plus className="h-4 w-4 mr-2" />
            Create Brand
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {brands.map((brand) => (
          <BrandCard
            key={brand.id}
            brand={brand}
            onEdit={onEdit}
            onDelete={onDelete}
            onView={onView}
          />
        ))}
      </div>
    </div>
  );
};
